/**
 * SKE-4807. The gate and the toast for "this update lost Instagram
 * publishing", shared by the reconnect thunks.
 *
 * The decision is the SERVER's: its token write stamps
 * `instagramPublishPermissionLostAt`, guarded against concurrent writes, and
 * every reconnect refetches the row. Comparing scope snapshots here instead
 * could disagree with that write when two reconnects overlap. With
 * `instagramPublishAuthenticationRequired` off, nothing is detected and every
 * surface says exactly what it said before.
 */
import { toast } from "react-toastify";

import { adminPanelFeatureToggle } from "../../utils/feature-toggle-utils";
import { PUBLISHING_LOST } from "./campaign-page-copy";

type LossStamped =
  | { instagramPublishPermissionLostAt?: string | Date | null }
  | null
  | undefined;

function stampTime(row: LossStamped): number | null {
  const at = row?.instagramPublishPermissionLostAt;
  return at ? new Date(at).getTime() : null;
}

/**
 * True only when the gate is on AND the refetched row carries a loss stamp the
 * row before the update did not: the server recorded a loss on this write.
 */
export function updateLostPublishing(
  before: LossStamped,
  after: LossStamped
): boolean {
  if (
    !adminPanelFeatureToggle.syncGet("instagramPublishAuthenticationRequired")
  ) {
    return false;
  }
  const lostAt = stampTime(after);
  return lostAt !== null && lostAt !== stampTime(before);
}

/**
 * The warning that replaces a success toast. It stays up for ten seconds
 * rather than the container's three, on the precedent of the OAuth error
 * toast, because it asks the customer to do something.
 */
export function warnPublishingLost(accountName: string): void {
  const pointer = adminPanelFeatureToggle.syncGet("instagramReconnectCampaign")
    ? PUBLISHING_LOST.toastCampaignPointer
    : "";
  toast.warning(`${PUBLISHING_LOST.toast(accountName)}${pointer}`, {
    autoClose: 10_000,
  });
}
